"use client";

import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { industries } from "@/data/industries";
import { PremiumCard } from "@/components/ui/PremiumCard";
import {
  Utensils,
  Zap,
  Wrench,
  Building2,
  Armchair,
  Rocket,
  Car,
  FlaskConical,
  Shirt,
  Mountain,
  FileText,
  Package,
  CircleDot,
  Pill,
  Gem,
  Monitor,
  Truck,
  GraduationCap,
  Hotel,
  UtensilsCrossed,
  Tractor,
  Store,
  Heart,
  HardHat,
  Settings,
  CarFront,
  Truck as T,
} from "lucide-react";
import type { Industry } from "@/types";

const ICON_COMPONENTS: Record<string, React.ComponentType<{ className?: string }>> = {
  Utensils,
  Zap,
  Wrench,
  Building2,
  Armchair,
  Rocket,
  Car,
  FlaskConical,
  Shirt,
  Mountain,
  FileText,
  Package,
  CircleDot,
  Pill,
  Gem,
  Monitor,
  Truck,
  GraduationCap,
  Hotel,
  UtensilsCrossed,
  Tractor,
  Store,
  Heart,
  HardHat,
  Settings,
  CarFront,
};

const INDUSTRY_ICON_COLORS: Record<string, string> = {
  "food-beverage": "text-[#059A8F]",
  "electrical": "text-[#D97706]",
  "metal": "text-[#7F77DD]",
  "building": "text-[#F97316]",
  "wood-furniture": "text-[#92400E]",
  "aerospace": "text-[#6D28D9]",
  "automotive": "text-[#0F8A6A]",
  "chemical": "text-[#7C3AED]",
  "textile": "text-[#2563EB]",
  "steel": "text-[#64748B]",
  "paper": "text-[#78716C]",
  "plastics": "text-[#D06B21]",
  "rubber": "text-[#5F7A16]",
  "pharma": "text-[#0B8B7D]",
  "jewellery": "text-[#B84A2D]",
  "it": "text-[#6D5BD0]",
  "logistics": "text-[#087B8F]",
  "edu": "text-[#2563EB]",
  "hotel-hospitality": "text-[#0F8A6A]",
  "restaurant": "text-[#C0267D]",
  "agriculture": "text-[#D97706]",
  "retail": "text-[#2563EB]",
  "healthcare": "text-[#0B8B7D]",
  "education": "text-[#2563EB]",
  "hospital": "text-[#0B8B7D]",
  "construction": "text-[#B84A2D]",
  "packaging": "text-[#2563EB]",
};

const CATEGORY_CARD_COLORS: Record<string, React.ComponentProps<typeof PremiumCard>["color"]> = {
  "Manufacturing": "blue",
  "FOOD & BEVERAGE": "blue",
  "Food & Beverage": "blue",
  "Electronics": "blue",
  "Metal": "blue",
  "Building": "blue",
  "Textile": "blue",
  "Auto": "blue",
  "Chem": "blue",
  "Pharma": "blue",
  "Retail": "blue",
  "Services": "blue",
  "Logistics": "blue",
  "Healthcare": "blue",
  "Construction": "blue",
  "Hospitality": "blue",
};

function isCategoryColorKey(value: string): value is keyof typeof CATEGORY_CARD_COLORS {
  return value in CATEGORY_CARD_COLORS;
}

function safeColor(value: Industry["category"]): React.ComponentProps<typeof PremiumCard>["color"] {
  const key = typeof value === "string" ? value : "Manufacturing";
  if (isCategoryColorKey(key)) return CATEGORY_CARD_COLORS[key];
  return "blue";
}

function IndustryIcon({ name, className }: { name: string; className?: string }) {
  const RawIcon = ICON_COMPONENTS[name] || Settings;
  return <RawIcon className={className} />;
}

function slugLookup(slug: string) {
  return slug.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

const ALL_INDUSTRIES_STATS = [
  { value: "35%", label: "Faster operations" },
  { value: "60%", label: "Lower defects" },
  { value: "20+", label: "Industries" },
];

function resolveStats(industry: Industry) {
  const custom: Record<string, { value: string; label: string }[]> = {
    "food-beverage": [
      { value: "35%", label: "Efficiency boost" },
      { value: "60%", label: "Quality improvement" },
      { value: "1000+", label: "Clients served" },
    ],
  };
  if (custom[industry.slug]) return custom[industry.slug];
  return industry.stats.length > 0 ? industry.stats.slice(0, 3) : ALL_INDUSTRIES_STATS;
}

function PremiumIndustryCard({ industry, index }: { industry: Industry; index: number }) {
  const iconName = typeof industry.icon === "string" ? industry.icon : "";
  const color = INDUSTRY_ICON_COLORS[slugLookup(industry.slug)] || "text-[#2073C1]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.45, delay: (index % 8) * 0.055, ease: [0.22, 1, 0.36, 1] }}
      className="group h-full"
    >
      <PremiumCard
        title={industry.title}
        description={industry.tagline}
        ctaLabel="Explore"
        href={industry.href}
        color={safeColor(industry.category)}
        customIcon={<IndustryIcon name={iconName} className={`h-10 w-10 ${color}`} />}
        className="h-full w-full transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.12),0_4px_16px_-4px_rgba(0,0,0,0.06)]"
      />
    </motion.div>
  );
}

export function IndustriesGrid() {
  return (
    <div id="all-industries" className="relative py-16 md:py-24 bg-white">
      <div className="container-site">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {industries.map((industry, index) => (
            <PremiumIndustryCard
              key={industry.id}
              industry={industry}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}